/**
 * useProductChannelSlots — Loads the existing per-channel slot assignments
 * for a product so callers can hydrate UI state from the DB.
 *
 * Wraps the existing `getChannelImages` API (`GET /products/{id}/channels/{channelId}/images`)
 * and exposes a normalized `ChannelSlotEntry[]` shape covering only filled
 * slots. Empty slots from the API response are filtered out so callers can
 * iterate without explicit empty-checks.
 *
 * QueryKey is prefixed with `product-channel-assets` to align with the
 * invalidation target used by `useChannelSlotMutation`, so writes to a slot
 * automatically refetch the hydrated state.
 */

import { useQuery } from '@tanstack/react-query'
import { getChannelImages, type ChannelSlot } from '@/lib/api/products'
import { getAssetUrl } from '@/lib/helpers/asset-urls'

export interface ChannelSlotEntry {
  slot: number
  asset_id: string
  asset_url: string
}

export function useProductChannelSlots(
  productId: string | null,
  channelId: string | null,
) {
  return useQuery<ChannelSlotEntry[]>({
    queryKey: ['product-channel-assets', productId, channelId],
    queryFn: async () => {
      const res = await getChannelImages(productId!, channelId!)
      // SG-10: deliver via the asset proxy (/api/v1/asset/{id}/thumb), never
      // the presigned `s3_url` from the API (expires after 24h → 403, and
      // leaks S3 into frontend state). `asset_id` is sufficient to render.
      return (res.slots ?? [])
        .filter((s: ChannelSlot) => !s.empty && !!s.asset_id)
        .map((s: ChannelSlot) => ({
          slot: s.slot,
          asset_id: s.asset_id as string,
          asset_url: getAssetUrl(s.asset_id as string, 'thumb'),
        }))
    },
    enabled: !!productId && !!channelId,
    staleTime: 30_000,
  })
}
